import PropTypes from 'prop-types';

import { useCommentsRoot } from 'components/pages/Offer/Comments/Root';

import useCommentIdFromRouterHash from 'hooks/useCommentIdFromRouterHash';
import useQueryClient from 'hooks/useQueryClient';
import useRatings from 'hooks/useRatings';

import { commentQueryKeys } from 'lib/queryKeys';

import { useDomain } from 'providers/DomainProvider';

import CommentItem from '../Item';
import LoadMoreReplies from './LoadMoreReplies';

const COMMENTS_REPLIES_LIMIT = 10;

const RepliesList = ({
  canCommentsBeRecommendedAsReview,
  hashCommentRef,
  parentId,
  replies,
}) => {
  const queryClient = useQueryClient();
  const { commentId: commentIdFromUrlHash } = useCommentIdFromRouterHash();
  const { commentsSort } = useCommentsRoot();
  const { domain, domainId, domainIdKey } = useDomain();
  const { isLiked, isDisliked } = useRatings({ domain, domainId });

  const appendRepliesAfterPagination = async (
    nextReplies,
    { parentCommentId }
  ) => {
    const { uniqBy } = await import('lib/array');

    queryClient.setInfiniteQueryData(
      commentQueryKeys[domain].list({
        [domainIdKey]: domainId,
        sortType: commentsSort,
      }),
      (pages) =>
        pages.map((commentsPage) =>
          commentsPage.map((comment) => {
            if (comment.commentId !== parentCommentId) {
              return comment;
            }

            const updatedComment = structuredClone(comment);

            /**
             * There is an edge case were any new comment added as a reply
             * will also come after the pagination, so we have to filter
             * that duplicate out of the array.
             */
            updatedComment.replies = uniqBy(
              comment.replies.concat(nextReplies),
              'commentId'
            );

            return updatedComment;
          })
        )
    );
  };

  const hasPagination = replies.length % COMMENTS_REPLIES_LIMIT === 0;
  const lastReplyId = replies.at(-1).commentId;

  return (
    <>
      {replies.map(
        ({
          commentDate,
          commentFixed,
          commentId,
          commentLikes,
          commentStatusName,
          commentText,
          edited,
          isReview,
          userId,
          userLevel,
          userName,
          userPhoto,
          userSpecialtyName,
          userTypeName,
          userUsername,
        }) => (
          <CommentItem
            key={commentId}
            ref={
              commentIdFromUrlHash === commentId ? hashCommentRef : undefined
            }
            canBeRecommendedAsReview={canCommentsBeRecommendedAsReview}
            commentDate={commentDate}
            commentFixed={commentFixed}
            commentId={commentId}
            commentLikes={commentLikes}
            commentStatusName={commentStatusName}
            commentText={commentText}
            hashCommentRef={hashCommentRef}
            isDisliked={isDisliked(commentId)}
            isEdited={edited}
            isLiked={isLiked(commentId)}
            isReview={isReview}
            parentId={parentId}
            replies={replies}
            userId={userId}
            userLevel={userLevel}
            userName={userName}
            userPhoto={userPhoto}
            userSpecialtyName={userSpecialtyName}
            userTypeName={userTypeName}
            userUsername={userUsername}
            isReply
          />
        )
      )}
      {hasPagination && (
        <LoadMoreReplies
          appendRepliesAfterPagination={appendRepliesAfterPagination}
          lastReplyId={lastReplyId}
          parentId={parentId}
        />
      )}
    </>
  );
};

RepliesList.propTypes = {
  canCommentsBeRecommendedAsReview: PropTypes.bool.isRequired,
  hashCommentRef: PropTypes.shape(),
  parentId: PropTypes.number.isRequired,
  replies: PropTypes.arrayOf(PropTypes.shape()).isRequired,
};

export default RepliesList;
